/**
 * One-page PDF of a user's 2FA backup codes, written by hand so saving codes
 * costs no PDF dependency in every app that enrols 2FA. Every string is forced
 * to ASCII because the xref table stores BYTE offsets — keeping the file
 * single-byte lets `string.length` be that offset.
 */

export interface BackupCodesPdfOptions {
  /** The codes, in the order they were issued. */
  codes: string[];
  /** Who the codes belong to (email / workspace), so a saved file is traceable. */
  accountLabel?: string | null;
  /** Product name in the title block and footer. */
  productName?: string;
  /** Download name, without the `.pdf` extension. Defaults to `<product>-backup-codes-<date>`. */
  fileName?: string;
  /** Overrides "now" for the generated-on line. */
  generatedAt?: Date;
}

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 56;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const COLUMNS = 2;

// Base-14 fonts declared in the page resources.
const REGULAR = 'F1';
const BOLD = 'F2';
const MONO = 'F3';

// Average glyph width as a fraction of font size — Courier is a true monospace
// at 0.6em; 0.5em is a safe over-estimate for Helvetica, so wrapping never
// overflows the column.
const REGULAR_RATIO = 0.5;

type Rgb = readonly [number, number, number];

const INK: Rgb = [0.09, 0.11, 0.15];
const MUTED: Rgb = [0.42, 0.45, 0.5];
const RULE: Rgb = [0.85, 0.87, 0.9];

const INTRO =
  'Each code signs you in once, and only when you cannot use your authenticator app. ' +
  'Keep this file private - anyone holding these codes can complete your second step.';

/** Folds the characters PDF base-14 fonts cannot show down to plain ASCII. */
function toAscii(value: string): string {
  return value
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2010-\u2015]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u00A0\u2007\u202F]/g, ' ')
    .replace(/[^\x20-\x7E]/g, '?');
}

function pdfString(value: string): string {
  return toAscii(value).replace(/([\\()])/g, '\\$1');
}

function drawText(x: number, y: number, size: number, font: string, value: string, color: Rgb = INK): string {
  const [r, g, b] = color;
  return `BT ${r} ${g} ${b} rg /${font} ${size} Tf 1 0 0 1 ${x} ${y} Tm (${pdfString(value)}) Tj ET`;
}

function drawRule(y: number): string {
  const [r, g, b] = RULE;
  return `${r} ${g} ${b} RG 0.75 w ${MARGIN} ${y} m ${PAGE_WIDTH - MARGIN} ${y} l S`;
}

function wrapText(value: string, size: number, maxWidth: number): string[] {
  const perLine = Math.max(1, Math.floor(maxWidth / (size * REGULAR_RATIO)));
  const lines: string[] = [];
  let line = '';
  for (const word of toAscii(value).split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (next.length <= perLine) {
      line = next;
      continue;
    }
    if (line) lines.push(line);
    line = word;
  }
  if (line) lines.push(line);
  return lines;
}

function assemble(objects: string[]): string {
  let file = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, index) => {
    offsets.push(file.length);
    file += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = file.length;
  const size = objects.length + 1;
  file += `xref\n0 ${size}\n0000000000 65535 f \n`;
  for (const offset of offsets) file += `${String(offset).padStart(10, '0')} 00000 n \n`;
  file += `trailer\n<< /Size ${size} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  return file;
}

/** Renders the codes as a printable PDF blob. */
export function buildBackupCodesPdf({
  codes,
  accountLabel,
  productName = 'Clarix',
  generatedAt,
}: BackupCodesPdfOptions): Blob {
  const generated = (generatedAt ?? new Date()).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const ops: string[] = [];
  let y = PAGE_HEIGHT - MARGIN - 24;

  ops.push(drawText(MARGIN, y, 11, REGULAR, productName.toUpperCase(), MUTED));
  y -= 26;
  ops.push(drawText(MARGIN, y, 20, BOLD, 'Two-factor backup codes'));
  y -= 20;
  for (const line of accountLabel ? wrapText(accountLabel, 11, CONTENT_WIDTH) : []) {
    ops.push(drawText(MARGIN, y, 11, REGULAR, line, MUTED));
    y -= 14;
  }
  ops.push(drawText(MARGIN, y, 11, REGULAR, `Generated ${generated}`, MUTED));

  y -= 18;
  ops.push(drawRule(y));
  y -= 22;

  for (const line of wrapText(INTRO, 11, CONTENT_WIDTH)) {
    ops.push(drawText(MARGIN, y, 11, REGULAR, line));
    y -= 15;
  }
  y -= 14;

  const rows = Math.ceil(codes.length / COLUMNS);
  const footerY = MARGIN + 16;
  const leading = Math.min(24, Math.max(12, (y - footerY - 12) / Math.max(rows, 1)));
  const columnWidth = CONTENT_WIDTH / COLUMNS;

  codes.forEach((code, index) => {
    const row = Math.floor(index / COLUMNS);
    const column = index % COLUMNS;
    const x = MARGIN + column * columnWidth;
    const lineY = y - row * leading;
    ops.push(drawText(x, lineY, 9, REGULAR, String(index + 1).padStart(2, '0'), MUTED));
    ops.push(drawText(x + 20, lineY, 13, MONO, code));
  });

  ops.push(drawRule(footerY + 18));
  ops.push(
    drawText(MARGIN, footerY, 9, REGULAR, `${productName} - keep this document private`, MUTED),
  );

  const stream = ops.join('\n');
  return toBlob(
    assemble([
      '<< /Type /Catalog /Pages 2 0 R >>',
      '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] ` +
        `/Resources << /Font << /${REGULAR} 4 0 R /${BOLD} 5 0 R /${MONO} 6 0 R >> >> /Contents 7 0 R >>`,
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
      '<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold /Encoding /WinAnsiEncoding >>',
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    ]),
  );
}

function toBlob(file: string): Blob {
  const bytes = new Uint8Array(file.length);
  for (let i = 0; i < file.length; i += 1) bytes[i] = file.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: 'application/pdf' });
}

function defaultFileName({ productName = 'Clarix', generatedAt }: BackupCodesPdfOptions): string {
  const slug = toAscii(productName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const day = (generatedAt ?? new Date()).toISOString().slice(0, 10);
  return `${slug || 'clarix'}-backup-codes-${day}`;
}

/** Builds the PDF and saves it to the user's downloads. Returns false if it could not. */
export function downloadBackupCodesPdf(options: BackupCodesPdfOptions): boolean {
  if (!options.codes.length) return false;
  try {
    const url = URL.createObjectURL(buildBackupCodesPdf(options));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${options.fileName ?? defaultFileName(options)}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoke a tick later — Safari cancels the download if the URL dies first.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}
